import { decodeToken } from './decode-token';
import { EnvironmentConfigType } from './relay-types';

const refreshToken = async (authUrl: string): Promise<string> => {
    const refreshUrl = `${authUrl}/refresh`;
    const refreshResponse = await fetch(refreshUrl, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
    });

    if (!refreshResponse.ok) {
        const errorData = await refreshResponse.json();
        const error = new Error(`Refresh failed: ${refreshResponse.status}`);
        (error as any).response = {
            status: refreshResponse.status,
            statusText: refreshResponse.statusText,
            data: errorData,
            headers: refreshResponse.headers,
        };
        throw error;
    }

    const refreshData = await refreshResponse.json();
    return refreshData.data;
};

const isTokenFresh = (token: string, gap: number = 20): boolean => {
    const decoded = decodeToken(token);
    if (!decoded) {
        // If the token cannot be decoded, assume it is fresh
        return true;
    }
    const exp = decoded.exp;
    const now = Math.floor(Date.now() / 1000);
    return exp - now >= gap;
};

let refreshPromise: Promise<string> | null = null;

export const tokenRefresher = async (config: EnvironmentConfigType): Promise<string> => {
    const storedToken = await config.loadToken();

    if (!storedToken) {
        return Promise.resolve('');
    }

    if (isTokenFresh(storedToken)) {
        return Promise.resolve(storedToken);
    }

    // If a refresh is already in progress, return the same promise.
    if (refreshPromise) {
        return refreshPromise;
    }

    refreshPromise = (async () => {
        const newToken = await refreshToken(config.authUrl);

        if (!newToken) {
            throw new Error('No new token returned from refresh');
        }

        await config.saveToken(newToken);
        return newToken;
    })();

    // Clear the refreshPromise once it resolves or fails.
    refreshPromise
        .then(() => {
            refreshPromise = null;
        })
        .catch(() => {
            refreshPromise = null;
        });

    return refreshPromise;
};
